import React, { useState } from 'react'
import { Check, X, HelpCircle } from 'lucide-react'
import KaTeXRenderer from '../../../common/KaTeXRenderer'
import { LessonStatsService } from '../../../../services/stats/LessonStatsService'
import { soundService } from '../../../../services/audio/SoundEffectService'

interface MultipleResponseQuestionProps {
  question: string
  options: string[]
  correctAnswers: number[]  // Array of correct answer indices
  explanation: string
  hint?: string
  difficulty?: 'easy' | 'medium' | 'hard'
  onComplete?: () => void
  lessonId?: string
}

/**
 * Multiple Response Question - Component cho câu hỏi chọn nhiều đáp án
 * Dùng chung cho tất cả các bài học
 */
const MultipleResponseQuestion: React.FC<MultipleResponseQuestionProps> = ({
  question,
  options,
  correctAnswers,
  explanation,
  hint,
  difficulty = 'medium',
  onComplete,
  lessonId = 'current-lesson'
}) => {
  // ✨ Shuffle options khi component mount để tránh ghi nhớ vị trí
  const [shuffledData] = useState(() => {
    const indices = options.map((_, i) => i)
    // Fisher-Yates shuffle
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]]
    }
    
    const shuffledOptions = indices.map(i => options[i])
    // Map correctAnswers to new positions
    const newCorrectAnswers = correctAnswers.map(oldIdx => indices.indexOf(oldIdx))
    
    return {
      options: shuffledOptions,
      correctAnswers: newCorrectAnswers,
      originalToShuffled: indices
    }
  })
  
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([])
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [showHint, setShowHint] = useState(false)
  const [hasRecordedStats, setHasRecordedStats] = useState(false)  // ✅ Đảm bảo chỉ ghi 1 lần

  const handleToggleAnswer = (index: number) => {
    if (isSubmitted) return // Không cho thay đổi sau khi submit

    if (selectedAnswers.includes(index)) {
      setSelectedAnswers(selectedAnswers.filter(i => i !== index))
    } else {
      setSelectedAnswers([...selectedAnswers, index])
    }
  }

  const handleSubmit = () => {
    if (selectedAnswers.length === 0) return
    setIsSubmitted(true)
    
    // Check if correct
    const isCorrect = 
      selectedAnswers.length === shuffledData.correctAnswers.length &&
      selectedAnswers.every(ans => shuffledData.correctAnswers.includes(ans))
    
    // 🔊 Phát âm thanh feedback
    if (isCorrect) {
      soundService.playCorrect()
    } else {
      soundService.playIncorrect()
    }
    
    // ✅ Ghi nhận vào stats CHỈ 1 LẦN
    if (!hasRecordedStats) {
      if (isCorrect) {
        LessonStatsService.recordCorrectAnswer(lessonId)
      } else {
        LessonStatsService.recordWrongAnswer(lessonId)
      }
      setHasRecordedStats(true)
    }
    
    if (isCorrect && onComplete) {
      setTimeout(() => onComplete(), 1500)
    }
  }

  const handleReset = () => {
    setSelectedAnswers([])
    setIsSubmitted(false)
    setShowHint(false)
  }

  const isCorrect = 
    isSubmitted &&
    selectedAnswers.length === shuffledData.correctAnswers.length &&
    selectedAnswers.every(ans => shuffledData.correctAnswers.includes(ans))

  const getOptionStyle = (index: number) => {
    if (!isSubmitted) {
      return selectedAnswers.includes(index)
        ? 'border-blue-500 bg-blue-50'
        : 'border-gray-300 bg-white hover:border-blue-300 hover:bg-blue-50'
    }

    // After submit
    const isSelected = selectedAnswers.includes(index)
    const shouldBeSelected = shuffledData.correctAnswers.includes(index)

    if (shouldBeSelected && isSelected) {
      return 'border-green-500 bg-green-50'  // Correct selection
    } else if (shouldBeSelected && !isSelected) {
      return 'border-orange-500 bg-orange-50'  // Missed correct answer
    } else if (!shouldBeSelected && isSelected) {
      return 'border-red-500 bg-red-50'  // Wrong selection
    } else {
      return 'border-gray-300 bg-gray-50'  // Not selected, not correct
    }
  }

  const getOptionIcon = (index: number) => {
    if (!isSubmitted) return null

    const isSelected = selectedAnswers.includes(index)
    const shouldBeSelected = shuffledData.correctAnswers.includes(index)

    if (shouldBeSelected && isSelected) {
      return <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
    } else if (shouldBeSelected && !isSelected) {
      return <span className="text-orange-600 font-bold flex-shrink-0">⚠️</span>
    } else if (!shouldBeSelected && isSelected) {
      return <X className="w-5 h-5 text-red-600 flex-shrink-0" />
    }
    return null
  }

  const getDifficultyColor = () => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'hard': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="bg-white rounded-lg border-2 border-gray-200 p-5 my-4 shadow-sm">
      {/* Header với difficulty badge */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg font-bold text-gray-900">❓</span>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${getDifficultyColor()}`}>
              {difficulty === 'easy' ? 'Dễ' : difficulty === 'medium' ? 'Trung bình' : 'Khó'}
            </span>
            <span className="text-xs px-2 py-1 rounded-full font-medium bg-purple-100 text-purple-800">
              Chọn nhiều đáp án
            </span>
          </div>
          <div className="text-base font-semibold text-gray-800 leading-relaxed">
            <KaTeXRenderer content={question} />
          </div>
        </div>
      </div>

      {/* Options */}
      <div className="space-y-3 mb-4">
        {shuffledData.options.map((option, index) => (
          <div
            key={index}
            onClick={() => handleToggleAnswer(index)}
            className={`
              flex items-start gap-3 p-4 rounded-lg border-2 transition-all cursor-pointer
              ${getOptionStyle(index)}
              ${!isSubmitted && 'active:scale-[0.99]'}
            `}
          >
            {/* Checkbox */}
            <div className="flex-shrink-0 mt-0.5">
              <div className={`
                w-5 h-5 rounded border-2 flex items-center justify-center
                ${selectedAnswers.includes(index) 
                  ? 'bg-blue-500 border-blue-500' 
                  : 'bg-white border-gray-400'
                }
              `}>
                {selectedAnswers.includes(index) && (
                  <Check className="w-4 h-4 text-white" strokeWidth={3} />
                )}
              </div>
            </div>

            {/* Option text */}
            <div className="flex-1 text-sm font-medium text-gray-800">
              <KaTeXRenderer content={option} />
            </div>

            {/* Result icon */}
            {getOptionIcon(index)}
          </div>
        ))}
      </div>

      {/* Hint button */}
      {hint && !isSubmitted && (
        <button
          onClick={() => setShowHint(!showHint)}
          className="mb-3 text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
        >
          <HelpCircle className="w-4 h-4" />
          {showHint ? 'Ẩn gợi ý' : 'Hiện gợi ý'}
        </button>
      )}

      {/* Hint */}
      {showHint && !isSubmitted && (
        <div className="mb-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <div className="text-sm text-yellow-800">
            <strong>💡 Gợi ý:</strong> <KaTeXRenderer content={hint || ''} />
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-3">
        {!isSubmitted ? (
          <button
            onClick={handleSubmit}
            disabled={selectedAnswers.length === 0}
            className={`
              px-6 py-2.5 rounded-lg font-semibold text-white transition-all
              ${selectedAnswers.length === 0
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 active:scale-95 shadow-md'
              }
            `}
          >
            Kiểm tra đáp án
          </button>
        ) : (
          <button
            onClick={handleReset}
            className="px-6 py-2.5 rounded-lg font-semibold bg-gray-600 text-white hover:bg-gray-700 active:scale-95 shadow-md transition-all"
          >
            Làm lại
          </button>
        )}

        <div className="text-sm text-gray-600">
          Đã chọn: <span className="font-bold text-blue-600">{selectedAnswers.length}</span> / {shuffledData.options.length}
        </div>
      </div>

      {/* Result */}
      {isSubmitted && (
        <div 
          className={`
            mt-4 p-4 rounded-lg border-2
            ${isCorrect 
              ? 'bg-green-50 border-green-300' 
              : 'bg-red-50 border-red-300'
            }
          `}
          style={{
            animation: 'slideUpFade 0.4s ease-out'
          }}
        >
          <div className="flex items-start gap-3">
            <div 
              className="flex-shrink-0 mt-0.5"
              style={isCorrect ? { animation: 'gentleBounce 0.6s ease-in-out' } : {}}
            >
              {isCorrect ? (
                <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                  <Check className="w-5 h-5 text-white" strokeWidth={3} />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                  <X className="w-5 h-5 text-white" strokeWidth={3} />
                </div>
              )}
            </div>
            <div className="flex-1">
              <h4 className={`font-bold text-base mb-2 ${isCorrect ? 'text-green-800' : 'text-red-800'}`}>
                {isCorrect ? '✅ Chính xác!' : '❌ Chưa đúng!'}
              </h4>
              <div className="text-sm text-gray-800 leading-relaxed">
                <strong>Giải thích:</strong> <KaTeXRenderer content={explanation} />
              </div>
              
              {!isCorrect && (
                <div className="mt-3 text-sm">
                  <p className="text-gray-700 font-medium mb-1">
                    Đáp án đúng: {shuffledData.correctAnswers.map(i => shuffledData.options[i].split('.')[0]).join(', ')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MultipleResponseQuestion

